import { queryKeys } from "@/lib/query-keys";
import {
  getNotifications,
  getUnreadNotificationsCount,
} from "@/lib/services/notifications";
import { Notification } from "@/lib/types/notification";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";

interface NotificationResponse {
  data: {
    data: Notification[];
    current_page: number;
    last_page: number;
  };
}

interface InfiniteNotificationData {
  pages: NotificationResponse[];
  pageParams: unknown[];
}

type NotificationCacheData = NotificationResponse | InfiniteNotificationData;

function isInfiniteData(
  old: NotificationCacheData,
): old is InfiniteNotificationData {
  return "pages" in old && Array.isArray(old.pages);
}

function mapNotifications(
  page: NotificationResponse,
  mapper: (n: Notification) => Notification,
): NotificationResponse {
  if (!page?.data?.data) return page;

  return {
    ...page,
    data: {
      ...page.data,
      data: page.data.data.map(mapper),
    },
  };
}

function updateNotificationCache(
  old: NotificationCacheData | undefined,
  mapper: (n: Notification) => Notification,
): NotificationCacheData | undefined {
  if (!old) return old;

  if (isInfiniteData(old)) {
    return {
      ...old,
      pages: old.pages.map((page) => mapNotifications(page, mapper)),
    };
  }

  return mapNotifications(old, mapper);
}

export function useNotifications(
  page: number = 1,
  options?: Omit<UseQueryOptions<NotificationResponse>, "queryKey" | "queryFn">,
) {
  return useQuery({
    queryKey: queryKeys.notifications.list(page),
    queryFn: () => getNotifications(page),
    staleTime: 60 * 1000,
    ...options,
  });
}

export function useInfiniteNotifications() {
  return useInfiniteQuery({
    queryKey: [...queryKeys.notifications.all, "infinite"],
    queryFn: ({ pageParam }) => getNotifications(pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.data.current_page;
      const lastPageNumber = lastPage.data.last_page;

      if (currentPage >= lastPageNumber) {
        return undefined;
      }

      return currentPage + 1;
    },
    staleTime: 60 * 1000,
  });
}

export function useUnreadNotificationsCount() {
  return useQuery({
    queryKey: queryKeys.notifications.unreadCount,
    queryFn: getUnreadNotificationsCount,
    staleTime: 60 * 1000,
  });
}

/** Call this after a mark-as-read to update the cache in-place without refetching. */
export function useMarkNotificationRead() {
  const qc = useQueryClient();

  return {
    markOne: (id: string) => {
      qc.setQueriesData<NotificationCacheData>(
        { queryKey: queryKeys.notifications.all },
        (old) =>
          updateNotificationCache(old, (n) =>
            n.id === id ? { ...n, read_at: new Date().toISOString() } : n,
          ),
      );
      qc.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount });
    },
    markAll: () => {
      qc.setQueriesData<NotificationCacheData>(
        { queryKey: queryKeys.notifications.all },
        (old) =>
          updateNotificationCache(old, (n) => ({
            ...n,
            read_at: n.read_at ?? new Date().toISOString(),
          })),
      );
      qc.setQueryData(queryKeys.notifications.unreadCount, 0);
    },
  };
}
